import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { I18nStore } from '../store/i18n.store';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="language-dropdown" [class.open]="isOpen()">
      <button
        type="button"
        class="dropdown-trigger"
        (click)="toggleDropdown()"
        [title]="getLanguageTitle(i18n.lang())"
      >
        <span class="selected-language">{{ i18n.lang().toUpperCase() }}</span>
        <span class="dropdown-arrow" [class.rotated]="isOpen()">▼</span>
      </button>

      @if (isOpen()) {
      <div class="dropdown-menu">
        @for (language of languages(); track language) { @if (language !== i18n.lang()) {
        <button
          type="button"
          class="dropdown-item"
          (click)="selectLanguage(language)"
          [title]="getLanguageTitle(language)"
        >
          <span class="language-code">{{ language.toUpperCase() }}</span>
          <span class="language-name">{{ getLanguageTitle(language) }}</span>
        </button>
        } }
      </div>
      }
    </div>
  `,
  styles: [
    `
      .language-dropdown {
        position: relative;
        display: inline-block;
      }

      .dropdown-trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #ddd;
        background: white;
        padding: 8px 12px;
        cursor: pointer;
        font-size: 12px;
        font-weight: 500;
        border-radius: 4px;
        transition: all 0.2s;
        min-width: 60px;
        justify-content: space-between;
      }

      .dropdown-trigger:hover {
        background: #f5f5f5;
        border-color: #1976d2;
      }

      .language-dropdown.open .dropdown-trigger {
        border-color: #1976d2;
        background: #f5f5f5;
      }

      .selected-language {
        font-weight: 600;
      }

      .dropdown-arrow {
        font-size: 10px;
        transition: transform 0.2s;
        color: #666;
      }

      .dropdown-arrow.rotated {
        transform: rotate(180deg);
      }

      .dropdown-menu {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        min-width: 140px;
        background: white;
        border: 1px solid #ddd;
        border-top: none;
        border-radius: 0 0 4px 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        overflow: hidden;
        white-space: nowrap;
      }

      .dropdown-item {
        width: 100%;
        border: none;
        background: white;
        padding: 10px 12px;
        cursor: pointer;
        font-size: 12px;
        text-align: left;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: background-color 0.2s;
        white-space: nowrap;
      }

      .dropdown-item:hover {
        background: #f0f8ff;
      }

      .language-code {
        font-weight: 600;
        min-width: 22px;
        flex-shrink: 0;
      }

      .language-name {
        color: #666;
        font-size: 11px;
        flex-shrink: 0;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherComponent {
  readonly i18n = inject(I18nStore);
  private readonly elementRef = inject(ElementRef);

  languages = input.required<string[]>();
  changed = output<string>();

  isOpen = signal(false);

  toggleDropdown(): void {
    this.isOpen.set(!this.isOpen());
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  async selectLanguage(lang: string): Promise<void> {
    try {
      await this.i18n.setLanguage(lang);
    } catch (error) {
      console.error('❌ Language switch failed:', error);
    }

    this.changed.emit(lang);
    this.closeDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  getLanguageTitle(lang: string): string {
    const titles: Record<string, string> = {
      en: 'English',
      de: 'Deutsch',
      fr: 'Français',
      es: 'Español',
      pt: 'Português',
      sq: 'Shqip',
      ar: 'العربية',
    };
    return titles[lang] || lang;
  }
}
